import { activitySummary } from "@closer/shared";
import { Card, Skeleton } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { formatDateTime } from "../lib/format";
import { usePreferences } from "../lib/preferences";
import { tenantKey } from "../lib/tenantQuery";

/**
 * Aktivite geçmişi — BAĞLAMSAL (CLOS-283 `[055]`).
 *
 * ### ⚠ Ayrı bir sayfa DEĞİL
 *
 * Kart: *"aktivite ayrı sidebar sayfası değildir; ilgili organizasyon profili,
 * kullanıcı/üyelik, departman veya rol BAĞLAMINDA gösterilir."* Bu yüzden bileşen
 * `entityType` + `entityId` zorunlu alıyor ve ilgili kaydın yanında duruyor. Bağlamsız bir
 * kullanım mümkün değil — tip düzeyinde de, uç düzeyinde de (uç bağlamsız çağrıyı 403
 * ile reddediyor).
 *
 * ### ⚠ Cümle SUNUCUDAN türetiliyor, satırdan değil
 *
 * Satır yalnız `summaryKey` + parametre taşıyor; Türkçe cümle `@closer/shared`'daki tek
 * fonksiyondan geliyor. Cümle veritabanına yazılsaydı bir metin düzeltmesi geçmiş
 * satırları eski ifadeyle bırakır ve aynı olay iki farklı cümleyle görünürdü.
 *
 * ### ⚠ Sessiz boş-durum YOK
 *
 * Sorgu düşerse bölüm *"yüklenemedi"* diyor; boş durum metni YALNIZ sunucu gerçekten sıfır
 * satır döndüğünde çıkıyor (CLOS-880). İkisi aynı ekranı çizseydi, bir arıza *"hiç işlem
 * olmamış"* gibi okunurdu — ve tam olarak öyle okundu.
 *
 * ### Yetkisiz satır HİÇ gelmiyor
 *
 * Süzme sunucuda (`lib/activity/access.ts`); burada gizlenecek bir şey yok. İstemcide
 * süzseydik veri zaten tarayıcıya inmiş olurdu.
 */

type ActivityItem = {
  id: string;
  sourceEventName: string;
  actorLabel: string | null;
  summaryKey: string;
  summaryParams: Record<string, unknown>;
  occurredAt: string;
  masked: boolean;
};

export function ActivityFeed({
  entityType,
  entityId,
  title = "Aktivite geçmişi",
}: {
  entityType: string;
  entityId: string;
  title?: string;
}) {
  const { activeTenantId } = useAuth();
  /** Tarih biçimi kullanıcının diline göre; sabit bir biçim başka bir dilde yanlış okunur. */
  const { preferences } = usePreferences();
  const tenantId = activeTenantId!;

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "activity", entityType, entityId),
    queryFn: async () =>
      (
        await api<{ items: ActivityItem[]; nextCursor: string | null }>(
          `/api/activity?entityType=${entityType}&entityId=${entityId}`,
          { tenantId },
        )
      ).data,
  });

  if (sorgu.isPending) {
    return (
      <Card title={title}>
        <div role="status" aria-label="Aktivite yükleniyor">
          <Skeleton lines={3} />
        </div>
      </Card>
    );
  }

  /**
   * ⚠ ARIZA GÖRÜNÜR (CLOS-880) — önceki karar geri alındı.
   *
   * `[055]` teslimi burada `return null` diyordu: *"aktivite bir bağlam bilgisi, sayfanın
   * asıl işini hata kutusuyla bölmeyelim."* Gerekçe iyi niyetliydi ama sonucu yanlıştı:
   * sorgu düştüğünde kullanıcı, olmuş işlemleri OLMAMIŞ gibi görüyordu — bölüm ya hiç
   * yoktu ya boştu, ikisi de *"kayıt yok"* diye okunuyor.
   *
   * Kapıyı besleyen sorgu düşerse kapı KAPANMALI: burada kapanmak, boş durum metnini
   * bastırıp arızayı söylemek demek. Sayfa hâlâ bölünmüyor — tek satır, kartın içinde.
   */
  if (sorgu.error || !sorgu.data) {
    return (
      <Card title={title}>
        <p role="alert" className="py-4 text-center text-sm text-danger">
          Aktivite geçmişi yüklenemedi
        </p>
      </Card>
    );
  }

  const items = sorgu.data.items;

  return (
    <Card title={title}>
      {items.length === 0 ? (
        <p className="py-4 text-center text-sm text-fg-muted">Henüz kayıtlı işlem yok</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-default">
          {items.map((item) => (
            <li key={item.id} data-testid="aktivite-satiri" className="flex flex-col gap-0.5 py-2">
              <p className="text-sm text-fg-strong">
                {activitySummary(item.summaryKey, item.summaryParams)}
              </p>
              <p className="text-xs text-fg-soft">
                {/*
                  Maskeli satırda aktör adı GELMİYOR (sunucu boşaltıyor) ve burada
                  uydurulmuyor: "bilinmeyen kullanıcı" yazmak, o gün o işi yapan birinin
                  olmadığını ima ederdi.
                */}
                {item.masked ? "Ayrıntılar gizli" : (item.actorLabel ?? "Sistem")} ·{" "}
                {formatDateTime(item.occurredAt, preferences.locale)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
